import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconDelete } from "../../../../../assets/icons";
import { Button, Form, Input, message, Spin, Switch } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { CheckOutlined, MenuOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import api from "../../../../../api/api";
import { localStorage } from "@utils";
import { useTranslation } from "react-i18next";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  margin-top: 24px;
  height: 100%;
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const ItemCard = styled.div`
  .ant-form {
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 24px 16px;
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 0;

      :nth-child(2),
      :nth-child(3) {
        width: 100%;
      }

      :first-child {
        width: 90px;

        * {
          width: 90px;
        }
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }
`;

const SpinWrapper = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const NoDataWrapper = styled.div`
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #868686;

  .title {
    font-size: 16px;
    font-weight: 500;
  }

  .desc {
    font-size: 14px;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

// const DraggableRow = (props) => {
//   const { attributes, listeners, setNodeRef, transform, transition } =
//     useSortable({ id: props["data-row-key"] });

//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     ...props.style,
//   };

//   return <tr {...props} ref={setNodeRef} style={style} />;
// };

const DragMenu = ({ attributes, listeners }) => {
  return (
    <span {...attributes} {...listeners}>
      <MenuOutlined className="drag-icon" style={{ cursor: "grab", fontSize: '18px' }} />
    </span>
  );
};


const Item = ({ index, item, handleUpdateClicked, handleDeleteClicked }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;

  // INITIALIZE SORTABLE HERE
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Translate.toString(transform), // Use Translate or Transform
    transition,
    zIndex: isDragging ? 999 : 1,
    opacity: isDragging ? 0.5 : 1,
    touchAction: "none", // Critical for mobile and pointer sensors
  };

  useEffect(() => {
    form.setFieldsValue({
      is_active: item.is_active,
      primary: item.primary,
      secondary: item.secondary,
    });
  }, [item, form]);

  const onFieldsChange = () => {
    handleUpdateClicked({
      id: item.id,
      is_new: item.is_new,
      ...form.getFieldsValue(),
    });
  };

  return (
    <ItemCard ref={setNodeRef} style={style}>
        <Form
        form={form}
        initialValues={{
          is_active: item.is_active,
          primary: item.primary,
          secondary: item.secondary,
        }}
        onFieldsChange={onFieldsChange}
      >        
     <DragMenu attributes={attributes} listeners={listeners} />
        
        <Form.Item style={{ width: "60px" }}>
          <Input value={index + 1} readOnly />
        </Form.Item>
        <Form.Item style={{ width: "50%" }} name={"primary"}>
          <Input placeholder={`${t('region_name')} (${storeLang[0]?.language})`} />
        </Form.Item>
        {langCount === 2 && (
          <Form.Item style={{ width: "50%" }} name={"secondary"}>
            <Input placeholder={`${t('region_name')} (${storeLang[1]?.language})`} />
          </Form.Item>
        )}
        <Form.Item name={"is_active"} valuePropName="checked">
          <Switch />
        </Form.Item>
        <IconDelete
          className="icon-delete"
          onClick={() => handleDeleteClicked(item)}
        />
      </Form>
    </ItemCard>
  );
};

const EditRegion = () => {
  const { city_id, city_name, country_name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [disabledSave, setDisabledSave] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [created, setCreated] = useState(false);
  const [edited, setEdited] = useState(false);
  const [editedCount, setEditedCount] = useState(0);
  const [deleted, setDeleted] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [itemsToDelete, setItemsToDelete] = useState([]);

  const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;
  
  const sensors = useSensors(useSensor(PointerSensor));  

  const {t} = useTranslation();

 const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => item.id === active.id);
    const newIndex = data.findIndex((item) => item.id === over.id);

    const newData = arrayMove(data, oldIndex, newIndex);

    // const sortPayload = {
    //   swap_id: newData.map((item, idx) => ({ id: item.id, sort: idx + 1 })),
    // };
  
    const activeId = event?.over?.id;
     const sortPayload = {
      swap_id: event?.active?.id,
    };

    api
      .updateRegionSort(activeId,sortPayload)
      .then(() => {
        getCityById(city_id)
        message.success(t('successfully_swapped'));
      })
      .catch((err) => {
        message.error(err);
      });
  };


  useEffect(() => {
    getCityById(city_id);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let _isEmpty = data.find((x) => isEmpty(x.primary) || (langCount === 2 && isEmpty(x.secondary)));
    setDisabledSave(_isEmpty || data.length === 0);

    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (
      editedCount !== 0 &&
      data.filter((x) => !x.is_new).length === editedCount
    ) {
      setEdited(true);
    }

    // eslint-disable-next-line
  }, [editedCount]);

  useEffect(() => {
    if (deletedCount !== 0 && itemsToDelete.length === deletedCount) {
      setDeleted(true);
    }

    // eslint-disable-next-line
  }, [deletedCount]);

  useEffect(() => {
    if (edited && created && deleted) {
      message.success(t('save_region_success'));
      goBack();
    }

    // eslint-disable-next-line
  }, [edited, created, deleted]);

  const getCityById = (city_id) => {
    api
      .getTownship({city_id:city_id})
      .then(({ data }) => {
        setLoading(false);
        setData([
          ...data.map((x) => {
            return {
              is_active: x.is_active,
              primary: x.name?.primary,
              secondary: langCount === 2 ? x.name?.secondary : null,
              id: x.id,
              is_new: false,
            };
          }),
        ]);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const isEmpty = (value) => {
    return !value || value?.trim() === "";
  };

  const addRegions = (values) => {
    if (values.length === 0) {
      setCreated(true);
      return;
    }
    let payload = {
      city_id,
      townships: values.map(({ is_active, primary, secondary }) => {
        return {
          is_active: is_active ? true : false,
          name: {
            primary,
            secondary,
          },
        };
      }),
    };
    api
      .createTownships(payload)
      .then(() => {
        setCreated(true);
      })
      .catch((error) => {
        setConfirmLoading(false);
        message.error(error);
      });
  };

  const editRegions = (values) => {
    if (values.length === 0) {
      setEdited(true);
    }
    values.forEach(({ id, is_active, primary, secondary }) => {
      let payload = {
        id,
        is_active: is_active ? true : false,
        name: {
          primary,
          secondary,
        },
      };
      api
        .editTownship(payload)
        .then(() => {
          setEditedCount((prev) => prev + 1);
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error);
        });
    });
  };

  const deleteRegions = (values) => {
    if (values.length === 0) {
      setDeleted(true);
    }
    values.forEach(({ id }) => {
      api
        .deleteTownship(id)
        .then(() => {
          setDeletedCount((prev) => prev + 1);
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error);
        });
    });
  };

  const handleSave = () => {
    if (!confirmLoading) {
      setConfirmLoading(true);
      let news = data.filter((x) => x.is_new);
      let olds = data.filter((x) => !x.is_new);
      addRegions(news);
      editRegions(olds);
      deleteRegions(itemsToDelete);
    }
  };

  const handleAddCityClicked = () => {
    setData((prev) => {
      return [
        ...prev,
        {
          id: uuid(),
          primary: "",
          secondary: "",
          is_active: true,
          is_new: true,
        },
      ];
    });
  };

  const handleUpdateClicked = (item) => {
    setData((prev) =>
      prev.map((x) => {
        if (x.id === item.id) {
          return item;
        }
        return x;
      })
    );
  };

  const handleDeleteClicked = (item) => {
    !item.is_new && setItemsToDelete((prev) => [...prev, item]);
    setData((prev) => prev.filter((x) => x.id !== item.id));
  };

  const goBack = () => {
    navigate(-1);
  };

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  const NoData = () => (
  <NoDataWrapper>
    <div className="title">{t('no_township')}</div>
    <div className="desc">{t('click_add_township')}</div>
    <Button
      type="primary"
      icon={<PlusCircleOutlined />}
      onClick={handleAddCityClicked}
    >
      {t("add_township")}
    </Button>
  </NoDataWrapper>
);

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          {t('back')}
        </Button>
        <TitleWrapper>
          <GrayTitle>{t('delivery_area')}</GrayTitle>
          <GrayTitle>{`>`}</GrayTitle>
          <Title>{t('edit_township')}</Title>
        </TitleWrapper>
        <Button
          type="primary"
          disabled={disabledSave}
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={confirmLoading}
        >
          {t('save')}
        </Button>
      </Topbar>
      {loading ? (
        <Loader />
      ) : (
        <>
          <TitleBar>
            <Title>{`${t('add_township_in')} ${city_name}, ${country_name}`}</Title>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={handleAddCityClicked}
            >
              {t('add_township')}
            </Button>
          </TitleBar>
         <Container>
          {data.length === 0 ? (
            <NoData />
          ) : (
              <DndContext
              sensors={sensors}
              onDragEnd={onDragEnd}
              collisionDetection={closestCenter}
            >
            <SortableContext
              items={data.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {data.map((item, key) => (
                <Item
                  key={item.id}
                  index={key}
                  item={item}
                  handleUpdateClicked={handleUpdateClicked}
                  handleDeleteClicked={handleDeleteClicked}
                />
              ))}
            </SortableContext>
          </DndContext>
            
          )}
        </Container>
        </>
      )}
    </RootContainer>
  );
};

export default EditRegion;
